import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";

import { FOCUS_RING, FOCUS_RING_GROUP } from "../lib/focus";
import { cn } from "../lib/utils";
import { Badge } from "./badge";
import { Icon, type IconComponent } from "./icon";

/**
 * v2 Tabs (redesign phase 5, #455). Storybook only until implementation;
 * see docs/design-library/04-implementation-notes.md for the swap.
 *
 * Tabs switch SECTIONS of a page, each with its own content. Switching a view
 * or filtering a value inside a section is the SegmentedControl.
 *
 * One underline style for both apps (break #93): labels Sora 14 Semibold in
 * every state, inactive `--ink-muted`, hover and active `--ink`; the active tab
 * has a 2px `--primary-accent` indicator over a 1px `--divider` baseline.
 * `--primary` would be 2.61:1 against the baseline in dark mode.
 *
 * Nothing about a trigger depends on its state (same weight, same padding, the
 * indicator is a pseudo-element, counts are tabular), so switching tabs cannot
 * move the labels ("tabs jump 17px", matrix).
 *
 * Overflow: the row scrolls horizontally (native scrollbar); the browser
 * scrolls a focused tab into view. Layout of the scroller:
 *   wrapper  `overflow-x-auto -mx-2 px-2`: clips, 8px wider than the list on
 *            each side so the label's focus ring (4px room + offset 2 + width 2)
 *            is not cut
 *   list     `min-w-full w-max`, the baseline as its bottom border, so the line
 *            spans the full width and grows with the tabs
 *   trigger  `-mb-px`, so the indicator lies ON the baseline, not above it
 * Labels start at the content edge (no trigger padding); the focus ring
 * surrounds the label with 4px room (`-mx-1 px-1`, so the label doesn't move).
 * Keyboard (arrows, Home/End, automatic activation) is Radix's.
 *
 * States are also styled by bare `data-hover` / `data-focus` for static
 * stories, as on Button; apps never set them.
 */

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(function TabsList({ className, ...props }, ref) {
  return (
    <div className="-mx-2 overflow-x-auto px-2">
      <TabsPrimitive.List
        ref={ref}
        className={cn("flex w-max min-w-full gap-6 border-b border-divider", className)}
        {...props}
      />
    </div>
  );
});

export interface TabsTriggerProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> {
  /** Leading icon, 16px, in the text colour. */
  icon?: IconComponent;
  /** Shown as an `xs` neutral Badge after the label; part of the accessible name. */
  count?: number;
}

const TabsTrigger = React.forwardRef<React.ElementRef<typeof TabsPrimitive.Trigger>, TabsTriggerProps>(
  function TabsTrigger({ icon, count, className, children, ...props }, ref) {
    return (
      <TabsPrimitive.Trigger
        ref={ref}
        className={cn(
          "group relative -mb-px flex h-10 shrink-0 cursor-pointer items-center whitespace-nowrap font-sans text-label text-ink-muted outline-hidden transition-colors",
          "hover:text-ink data-hover:text-ink data-[state=active]:text-ink",
          "after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:rounded-full data-[state=active]:after:bg-primary-accent",
          "disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
        {...props}
      >
        <span
          className={cn(
            "-mx-1 flex items-center gap-1.5 rounded-sm px-1",
            FOCUS_RING_GROUP,
          )}
        >
          {icon && <Icon icon={icon} />}
          {children}
          {count != null && (
            <>
              {/* Word break for the accessible name ("Fehler 12", not "Fehler12"); flex layout doesn't render it. */}{" "}
              <Badge size="xs" className="tabular-nums">
                {count.toLocaleString("de-DE")}
              </Badge>
            </>
          )}
        </span>
      </TabsPrimitive.Trigger>
    );
  },
);

/** No padding: spacing below the tabs is the page's. Focusable per Radix, so it gets the ring. */
const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(function TabsContent({ className, ...props }, ref) {
  return (
    <TabsPrimitive.Content
      ref={ref}
      className={cn(
        "rounded-sm outline-hidden",
        FOCUS_RING,
        className,
      )}
      {...props}
    />
  );
});

export { Tabs, TabsList, TabsTrigger, TabsContent };
